import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fetchAuthSession, fetchAuthToken, isAuthConfigured, requireAuth } from '../lib/auth.js';

/**
 * Authentication state for the whole app.
 *
 * Neon Auth owns credentials and the session cookie; this provider mirrors the
 * session into React state and exposes the short-lived JWT the API calls attach
 * as a bearer token. The token expires in 15 minutes, so it is fetched on demand
 * rather than cached alongside the session.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(!isAuthConfigured);

  const refresh = useCallback(async () => {
    if (!isAuthConfigured) return;
    // `getSession()`/`token()` on the SDK build the wrong path for this
    // deployment, so the session and JWT are read from Neon Auth's own routes
    // (see src/lib/auth.js). Sign-in/sign-up still go through the SDK.
    const current = await fetchAuthSession();
    // Neon answers 200 with a `null` body when there is no valid session, so
    // only a payload carrying a user counts as signed in.
    setSession(current?.user ? current : null);
    setToken(await fetchAuthToken());
  }, []);

  useEffect(() => {
    if (!isAuthConfigured) return undefined;
    let active = true;
    // Neon Auth persists the session in an HTTP-only cookie the SPA cannot
    // observe, so there is no change event to subscribe to: read it once on
    // mount, and again after every explicit sign-in/sign-out via `refresh()`.
    refresh()
      .catch(() => undefined)
      .finally(() => {
        if (active) setReady(true);
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  const signIn = useCallback(
    (email, password) => requireAuth().auth.signIn.email({ email, password }),
    [],
  );

  const signUp = useCallback(
    (email, password) =>
      requireAuth().auth.signUp.email({ name: email.split('@')[0] || 'User', email, password }),
    [],
  );

  const signOut = useCallback(async () => {
    await requireAuth().auth.signOut();
    setSession(null);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      token,
      ready,
      isConfigured: isAuthConfigured,
      refresh,
      signIn,
      signUp,
      signOut,
    }),
    [session, token, ready, refresh, signIn, signUp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context; throws outside :func:`AuthProvider`. */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}
